import React, { useEffect, useState, useRef } from "react";
import axios from "../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { FiChevronRight, FiPackage } from "react-icons/fi";
import { useAuth } from "../../Components/Context/AuthContext";

// ─── Loading Skeleton ─────────────────────────────────────────
const CategorySkeleton = () => (
  <div className="rounded-2xl border border-gray-200 overflow-hidden animate-pulse bg-white">
    <div className="aspect-square bg-gray-200" />
    <div className="p-3 space-y-2">
      <div className="h-4 w-3/4 bg-gray-200 rounded" />
      {/* <div className="h-3 w-1/2 bg-gray-200 rounded" /> */}
    </div>
  </div>
);

const EmptyRightState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">

    {/* ICON */}
    <div className="w-20 h-20 rounded-full bg-primaryColor/10 flex items-center justify-center mb-4">
      <svg
        className="w-10 h-10 text-primaryColor"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>

    {/* TEXT */}
    <h3 className="text-lg font-semibold text-gray-800 mb-1">
      Start Exploring Products
    </h3>

    <p className="text-sm text-gray-500 max-w-sm">
      Select a category from the left to browse products, varieties, and post your requirement easily.
    </p>

  </div>
);

const EmptyState = ({ title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">

    {/* ICON */}
    <div className="w-24 h-24 rounded-full bg-primaryColor/10 flex items-center justify-center mb-4">
      <svg
        className="w-12 h-12 text-primaryColor"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 7h18M3 12h18M3 17h18"
        />
      </svg>
    </div>

    <h3 className="text-lg font-semibold text-gray-800 mb-1">
      {title}
    </h3>

    <p className="text-sm text-gray-500 max-w-sm">
      {subtitle}
    </p>
  </div>
);

const ProductCard = ({ item, type, onClick, isActive }) => {
  const getImage = () => item.img;

  const getName = () => {
    if (type === "variety") return item.varietyName;
    return item.name;
  };

  return (
    <div
      onClick={onClick}
      className={`group cursor-pointer rounded-2xl overflow-hidden border transition-all duration-300 bg-white
      ${isActive
          ? "border-primaryColor shadow-lg scale-[1.02]"
          : "border-gray-200 hover:border-primaryColor/40 hover:shadow-md"
        }`}
    >
      {/* IMAGE */}
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {getImage() ? (
          <>
            <img
              src={getImage()}
              alt={getName()}
              className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
            />

            {/* GRADIENT OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="flex flex-col items-center justify-center text-gray-400">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center">
                <FiPackage className="text-3xl text-primaryColor" />
              </div>

              <p className="mt-3 text-xs font-medium text-gray-500 px-2 text-center">
                No Image
              </p>
            </div>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="p-3">
        <h3 className="font-semibold text-sm text-gray-800 line-clamp-2">
          {getName()}
        </h3>

        {type === "variety" && (
          <div className="mt-2 flex items-center gap-4 justify-between text-xs text-gray-500">
            <span>{item.grade}</span>
            <span>
              Minimum Order Quantity: {item?.slabAdjustedMOQ} {item.standardizedUNIT}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Products Component ──────────────────────────────────
const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [varieties, setVarieties] = useState([]);

  const [selected, setSelected] = useState({
    category: searchParams.get('category') || null,
    subCategory: searchParams.get('subCategory') || null,
    product: searchParams.get('product') || null,
    variety: searchParams.get('variety') || null,
  });

  const [loading, setLoading] = useState({
    category: false,
    sub: false,
    product: false,
    variety: false,
  });

  const [error, setError] = useState({
    category: false,
    sub: false,
    product: false,
    variety: false,
  });

  // Breadcrumb data
  const [breadcrumb, setBreadcrumb] = useState({
    category: null,
    subCategory: null,
    product: null,
    variety: null,
  });

  // ─── Fetch Categories ─────────────────────────────────────────
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading((p) => ({ ...p, category: true }));
        setError((p) => ({ ...p, category: false }));
        const { data } = await axios.get("/category/get-all", {
          params: { special: "all" },
        });
        setCategories(data.data || []);
      } catch (e) {
        setError((p) => ({ ...p, category: true }));
        toast.error("Failed to load categories");
      } finally {
        setLoading((p) => ({ ...p, category: false }));
      }
    };
    fetchCategories();
  }, []);

  // ─── Fetch Sub Categories ─────────────────────────────────────
  useEffect(() => {
    if (!selected.category) return;
    const fetchSub = async () => {
      try {
        setLoading((p) => ({ ...p, sub: true }));
        setError((p) => ({ ...p, sub: false }));
        setSubCategories([]);
        setProducts([]);
        setVarieties([]);
        const { data } = await axios.get(`/sub-category/get-sub-category-by-category/${selected.category}`);
        setSubCategories(data.data || []);
      } catch {
        setError((p) => ({ ...p, sub: true }));
        toast.error("Failed to load sub categories");
      } finally {
        setLoading((p) => ({ ...p, sub: false }));
      }
    };
    fetchSub();
  }, [selected.category]);

  // ─── Fetch Products ───────────────────────────────────────────
  useEffect(() => {
    if (!selected.subCategory) return;
    const fetchProduct = async () => {
      try {
        setLoading((p) => ({ ...p, product: true }));
        setError((p) => ({ ...p, product: false }));
        setProducts([]);
        setVarieties([]);
        const { data } = await axios.get(`/product/get-product-by-sub-category/${selected.subCategory}`);
        setProducts(data.data || []);
      } catch {
        setError((p) => ({ ...p, product: true }));
        toast.error("Failed to load products");
      } finally {
        setLoading((p) => ({ ...p, product: false }));
      }
    };
    fetchProduct();
  }, [selected.subCategory]);

  // ─── Fetch Varieties ──────────────────────────────────────────
  useEffect(() => {
    if (!selected.product) return;
    const fetchVariety = async () => {
      try {
        setLoading((p) => ({ ...p, variety: true }));
        setError((p) => ({ ...p, variety: false }));
        setVarieties([]);
        const { data } = await axios.get(`/product-variety/get-productVarieties-by-product/${selected.product}`);
        setVarieties(data.data || []);
      } catch {
        setError((p) => ({ ...p, variety: true }));
        toast.error("Failed to load varieties");
      } finally {
        setLoading((p) => ({ ...p, variety: false }));
      }
    };
    fetchVariety();
  }, [selected.product]);

  // ─── Update Breadcrumb ────────────────────────────────────────
  useEffect(() => {
    const cat = categories.find(c => c._id === selected.category);
    const sub = subCategories.find(s => s._id === selected.subCategory);
    const prod = products.find(p => p._id === selected.product);
    const variety = varieties.find(v => v._id === selected.variety);

    setBreadcrumb({
      category: cat || null,
      subCategory: sub || null,
      product: prod || null,
      variety: variety || null,
    });
  }, [selected, categories, subCategories, products, varieties]);

  useEffect(() => {
    // if (!selected.category) return;
    const newSelected = {
      category: searchParams.get('category') || null,
      subCategory: searchParams.get('subCategory') || null,
      product: searchParams.get('product') || null,
      variety: searchParams.get('variety') || null,
    };

    setSelected(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newSelected)) return prev;
      return newSelected;
    });
  }, [searchParams]);

  const selectedVarietyRef = useRef(null);
  const scrolledVarietyIdRef = useRef(null);

  // Reset scroll tracker when location/search changes so new navigations can scroll
  useEffect(() => {
    scrolledVarietyIdRef.current = null;
  }, [location.search, location.key]);

  // Smooth scroll to selected variety card when it becomes ready in the DOM
  useEffect(() => {
    if (selected.variety && breadcrumb.variety && !loading.variety) {
      if (scrolledVarietyIdRef.current !== selected.variety) {
        scrolledVarietyIdRef.current = selected.variety;

        const timer = setTimeout(() => {
          if (selectedVarietyRef.current) {
            selectedVarietyRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 200);

        return () => clearTimeout(timer);
      }
    } else if (!selected.variety) {
      scrolledVarietyIdRef.current = null;
    }
  }, [selected.variety, breadcrumb.variety, loading.variety]);

  // ─── Handlers ─────────────────────────────────────────────────
  const handleCategorySelect = (id) => {
    setSelected({
      category: id,
      subCategory: null,
      product: null,
      variety: null,
    });
    setSearchParams({ category: id });
  };

  const handleSubCategorySelect = (id) => {
    setSelected(prev => ({
      ...prev,
      subCategory: id,
      product: null,
      variety: null,
    }));
    setSearchParams({
      category: selected.category,
      subCategory: id
    });
  };

  const handleProductSelect = (id) => {
    setSelected(prev => ({
      ...prev,
      product: id,
      variety: null,
    }));
    setSearchParams({
      category: selected.category,
      subCategory: selected.subCategory,
      product: id
    });
  };

  const handleVarietySelect = (id) => {
    setSelected(prev => ({
      ...prev,
      variety: id,
    }));
    setSearchParams({
      category: selected.category,
      subCategory: selected.subCategory,
      product: selected.product,
      variety: id
    });
    setTimeout(() => {
      selectedVarietyRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePostRequirement = () => {
    if (selected.variety) {
      navigate(`/post-requirement?variety=${selected.variety}&product=${selected.product}&subCategory=${selected.subCategory}&category=${selected.category}&locked=true`);
    } else if (selected.product) {
      navigate(`/post-requirement?product=${selected.product}&subCategory=${selected.subCategory}&category=${selected.category}&locked=true`);
    } else {
      toast.error("Please select a product variety first");
    }
  };

  const handlePlaceRQMOrder = () => {
    if (selected.variety) {
      navigate(`/post-rqm-request?variety=${selected.variety}&product=${selected.product}&subCategory=${selected.subCategory}&category=${selected.category}`);
    } else if (selected.product) {
      navigate(`/post-rqm-request?product=${selected.product}&subCategory=${selected.subCategory}&category=${selected.category}`);
    } else {
      toast.error("Please select a product variety first");
    }
  };

  const handleBreadcrumbClick = (level) => {
    if (level === 'category') {
      setSelected({
        category: selected.category,
        subCategory: null,
        product: null,
        variety: null,
      });
      setSearchParams({ category: selected.category });
    } else if (level === 'subCategory') {
      setSelected(prev => ({
        ...prev,
        product: null,
        variety: null,
      }));
      setSearchParams({
        category: selected.category,
        subCategory: selected.subCategory
      });
    } else if (level === 'product') {
      setSelected(prev => ({
        ...prev,
        variety: null,
      }));
      setSearchParams({
        category: selected.category,
        subCategory: selected.subCategory,
        product: selected.product
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 mb-16 md:mb-0">

        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-primaryColor mb-1 md:mb-2">
            Browse Products
          </h1>
          <p className="text-gray-600 text-sm">
            Select category to explore our wide range of products
          </p>
        </div>

        {/* Breadcrumb */}
        {(breadcrumb.category || breadcrumb.subCategory || breadcrumb.product || breadcrumb.variety) && (
          <div className="sticky top-3 md:top-[3.5rem] z-10 mb-6 flex flex-wrap items-center gap-2 text-sm 
bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={() => {
                setSelected({ category: null, subCategory: null, product: null, variety: null });
                setSearchParams({});
              }}
              className="text-gray-500 hover:text-primaryColor transition"
            >
              Home
            </button>

            {breadcrumb.category && (
              <>
                <FiChevronRight className="text-gray-400" />
                <button
                  onClick={() => handleBreadcrumbClick('category')}
                  className={`${!breadcrumb.subCategory ? 'text-primaryColor font-semibold' : 'text-gray-600 hover:text-primaryColor'
                    } transition`}
                >
                  {breadcrumb.category.name}
                </button>
              </>
            )}

            {breadcrumb.subCategory && (
              <>
                <FiChevronRight className="text-gray-400" />
                <button
                  onClick={() => handleBreadcrumbClick('subCategory')}
                  className={`${!breadcrumb.product ? 'text-primaryColor font-semibold' : 'text-gray-600 hover:text-primaryColor'
                    } transition`}
                >
                  {breadcrumb.subCategory.name}
                </button>
              </>
            )}

            {breadcrumb.product && (
              <>
                <FiChevronRight className="text-gray-400" />
                <button
                  onClick={() => handleBreadcrumbClick('product')}
                  className={`${!breadcrumb.variety ? 'text-primaryColor font-semibold' : 'text-gray-600 hover:text-primaryColor'
                    } transition`}
                >
                  {breadcrumb.product.name}
                </button>
              </>
            )}

            {breadcrumb.variety && (
              <>
                <FiChevronRight className="text-gray-400" />
                <span className="text-primaryColor font-semibold">
                  {breadcrumb.variety.varietyName}
                </span>
              </>
            )}
          </div>
        )}

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Sidebar - Categories (Desktop) */}
          <div className="hidden lg:block lg:col-span-3 space-y-2 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto">
            <h3 className="font-semibold text-gray-800 mb-3 px-2">Categories</h3>

            {loading.category && (
              <div className="space-y-2">
                {Array(6).fill(0).map((_, i) => (
                  <CategorySkeleton key={i} />
                ))}
              </div>
            )}

            {!loading.category && categories.length > 0 && (
              <div className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat._id}
                    onClick={() => handleCategorySelect(cat._id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${selected.category === cat._id
                      ? 'bg-primaryColor text-white shadow-md'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                      }`}
                  >
                    <img
                      src={cat.img}
                      alt={cat.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">{cat.name}</p>
                    </div>
                    <FiChevronRight className={`text-lg ${selected.category === cat._id ? 'opacity-100' : 'opacity-50'
                      }`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 md:h-[calc(100vh-5rem)] overflow-y-auto px-2">

            {/* EMPTY STATE (DESKTOP ONLY) */}
            {!selected.category && (
              <div className="hidden lg:block">
                <EmptyRightState />
              </div>
            )}

            {/* Mobile Category Selector */}
            <div className="lg:hidden mb-5 md:mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={selected.category || ''}
                onChange={(e) => handleCategorySelect(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor outline-none"
              >
                <option value="">Choose a category...</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Categories Grid (Mobile) */}
            {!selected.category && (
              <div className="lg:hidden">
                <h3 className="font-semibold text-gray-800 mb-4">All Categories</h3>
                {loading.category && (
                  <div className="grid grid-cols-2 gap-3">
                    {Array(6).fill(0).map((_, i) => (
                      <CategorySkeleton key={i} />
                    ))}
                  </div>
                )}

                {!loading.category && categories.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {categories.map((cat) => (
                      <ProductCard
                        key={cat._id}
                        item={cat}
                        type="category"
                        onClick={() => handleCategorySelect(cat._id)}
                        isActive={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sub Categories */}
            {selected.category && !selected.subCategory && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 text-lg">
                    Sub Categories
                  </h3>
                </div>

                {loading.sub && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Array(8).fill(0).map((_, i) => (
                      <CategorySkeleton key={i} />
                    ))}
                  </div>
                )}

                {!loading.sub && subCategories.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {subCategories.map((sub) => (
                      <ProductCard
                        key={sub._id}
                        item={sub}
                        type="subCategory"
                        onClick={() => handleSubCategorySelect(sub._id)}
                        isActive={false}
                      />
                    ))}
                  </div>
                )}

                {!loading.sub && subCategories.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <EmptyState
                      title="No Sub Categories Found"
                      subtitle="This category currently has no sub categories available."
                    />
                  </div>
                )}
              </div>
            )}

            {/* Products */}
            {selected.subCategory && !selected.product && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 text-lg">
                    Products
                  </h3>
                </div>

                {loading.product && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Array(8).fill(0).map((_, i) => (
                      <CategorySkeleton key={i} />
                    ))}
                  </div>
                )}

                {!loading.product && products.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {products.map((prod) => (
                      <ProductCard
                        key={prod._id}
                        item={prod}
                        type="product"
                        onClick={() => handleProductSelect(prod._id)}
                        isActive={false}
                      />
                    ))}
                  </div>
                )}

                {!loading.product && products.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <EmptyState
                      title="No Products Found"
                      subtitle="No products available under this sub category."
                    />
                  </div>
                )}
              </div>
            )}

            {/* Product Varieties */}
            {selected.product && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h3 className="font-semibold text-gray-800 text-lg">
                    Product Varieties
                  </h3>
                  {/* <button
                    onClick={handlePostRequirement}
                    className="px-6 py-2.5 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                  >
                    Ask For A Quick Quotation
                  </button> */}
                </div>

                {loading.variety && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Array(8).fill(0).map((_, i) => (
                      <CategorySkeleton key={i} />
                    ))}
                  </div>
                )}

                {!loading.variety && varieties.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {varieties.map((variety) => (
                      <ProductCard
                        key={variety._id}
                        item={variety}
                        type="variety"
                        onClick={() => handleVarietySelect(variety._id)}
                        isActive={selected.variety === variety._id}
                      />
                    ))}
                  </div>
                )}

                {!loading.variety && varieties.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <EmptyState
                      title="No Varieties Found"
                      subtitle="No product varieties available for this product."
                    />
                  </div>
                )}
              </div>
            )}

            {/* Selected Variety Detail */}
            {selected.variety && breadcrumb.variety && (
              <div
                ref={selectedVarietyRef}
                className="mt-6 bg-white rounded-xl border-2 border-primaryColor shadow-lg p-6 scroll-mt-24 md:scroll-mt-28"
              >
                {/* Product Details */}
                <div className="flex flex-col md:flex-row gap-6">
                  {breadcrumb.variety.img ? (
                    <img
                      src={breadcrumb.variety.img}
                      alt={breadcrumb.variety.varietyName}
                      className="w-full md:w-64 h-64 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full md:w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <FiPackage className="text-3xl text-primaryColor" />
                        <p className="text-sm mt-2">No Image Available</p>
                      </div>
                    </div>
                  )}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-primaryColor mb-2">
                        {breadcrumb.variety.varietyName}
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <p className="text-gray-600">
                          Grade: <br /> <span className="font-semibold">{breadcrumb.variety.grade}</span>
                        </p>
                        <div>
                          <p className="text-sm text-gray-500">Minimum Order Quantity</p>
                          <p className="font-semibold text-lg">
                            {breadcrumb.variety.slabAdjustedMOQ} {breadcrumb.variety.standardizedUNIT}
                          </p>
                        </div>
                      </div>
                      <p className="text-primaryColor capitalize bg-primaryColor/10 mt-2 p-2 rounded-lg text-sm">
                        <span className="font-semibold">Description:</span> {breadcrumb.variety.description || 'No Description Given'}
                      </p>
                    </div>
                    {user?.role === "supplier" ? (
                      <button
                        onClick={handlePlaceRQMOrder}
                        className="mt-4 w-full md:w-auto px-8 py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        Place an RQM order
                      </button>
                    ) : (
                      <button
                        onClick={handlePostRequirement}
                        className="mt-4 w-full md:w-auto px-8 py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                      >
                        Ask For A Quick Quotation
                      </button>
                    )}
                  </div>
                </div>

                {/* ✅ COMMISSION DISCLAIMER - PROMINENT VERSION (Hidden for Suppliers) */}
                {user?.role !== "supplier" && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-300 dark:border-amber-500/40 p-4">
                      <div className="flex gap-3">
                        {/* Warning Icon */}
                        <div className="flex-shrink-0 flex items-start pt-0.5">
                          <svg
                            className="w-5 h-5 text-amber-600 dark:text-amber-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>

                        {/* Text */}
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-900 dark:text-amber-200 text-sm mb-1">
                            Disclaimer:
                          </h4>
                          <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed mb-2">
                            Dear buyer, you can order any quantity above {breadcrumb.variety.slabAdjustedMOQ} {breadcrumb.variety.standardizedUNIT}, however to get lowest quotation prices your order quantity should be above {breadcrumb.variety.minimum_MOQ} {breadcrumb.variety.standardizedUNIT}.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;